import React, { useState, useEffect, useRef, useCallback } from 'react';
import './HowToThumbnails.css';
import { getApiBase, fetchWithTimeout } from './apiBase';

const API_BASE = getApiBase();

function VideoSlide({ src, title, isActive }) {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isActive) {
      video.currentTime = 0;
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [isActive]);

  return (
    <div className="howto-slide">
      <video
        ref={videoRef}
        className="howto-slide-video"
        src={src}
        loop
        muted
        playsInline
        preload={isActive ? 'auto' : 'metadata'}
      />
      {title && <span className="howto-slide-title">{title}</span>}
    </div>
  );
}

function HowToThumbnails() {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const ac = new AbortController();
    fetchWithTimeout(`${API_BASE.replace(/\/$/, '')}/api/videos/trending`, { signal: ac.signal, timeout: 15000 })
      .then((res) => {
        const ct = res.headers.get('content-type') || '';
        if (!ct.includes('application/json')) return { ok: false, options: [] };
        return res.json();
      })
      .then((data) => {
        if (!cancelled && data && data.ok && Array.isArray(data.options)) {
          setOptions(data.options.filter((o) => o.video_url).slice(0, 20));
        }
        if (!cancelled) setError('');
      })
      .catch(() => {
        if (!cancelled) setError('');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; ac.abort(); };
  }, []);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !el.children.length) return;
    const scrollTop = el.scrollTop;
    const slideHeight = el.clientHeight;
    if (slideHeight > 0) {
      setActiveIndex(Math.round(scrollTop / slideHeight));
    }
  }, []);

  const base = API_BASE.replace(/\/$/, '');
  const videoSrc = (url) => {
    if (!url) return '';
    return url.startsWith('http') ? url : base + (url.startsWith('/') ? url : '/' + url);
  };

  const hasVideos = options.length > 0;

  return (
    <div className="howto-thumbnails">
      {loading && <p className="howto-thumbnails-muted">Loading…</p>}
      {error && <p className="howto-thumbnails-error">{error}</p>}
      {!loading && !hasVideos && (
        <p className="howto-thumbnails-muted">No videos yet. Generate one from Ask Aria.</p>
      )}
      <div className="howto-thumbnails-scroll" ref={scrollRef} onScroll={onScroll}>
        {options.map((opt, i) => (
          <VideoSlide
            key={opt.video_url || i}
            src={videoSrc(opt.video_url)}
            title={opt.title || ''}
            isActive={i === activeIndex}
          />
        ))}
      </div>
      {hasVideos && options.length > 1 && (
        <div className="howto-dots">
          {options.map((_, i) => (
            <span
              key={i}
              className={`howto-dot ${i === activeIndex ? 'howto-dot--active' : ''}`}
              onClick={() => {
                const el = scrollRef.current;
                if (el) el.scrollTo({ top: i * el.clientHeight, behavior: 'smooth' });
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default React.memo(HowToThumbnails);
